import React, { FormEvent, useEffect, useReducer, useState } from 'react';

import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { FileEvent } from '@components/buttons/BrowseFilesButton';
import Button, { ButtonStyle } from '@components/buttons/Button/Button';
import SubmitBtn from '@components/buttons/FormButtons/SubmitBtn';
import { WarningBox } from '@components/cards/messageBox';
import Checkbox from '@components/inputs/Checkbox';
import DropDownMenu from '@components/inputs/DropDownMenu';
import Label from '@components/inputs/text/Label';
import WarningModal from '@components/modal/info/Warning';
import { NavBar } from '@components/navigation/NavBar/NavBar';
import UploadFilePanel from '@components/panels/UploadFile/UploadFilePanel';
import Elliptical from '@components/spinners/Elliptical/Elliptical';
import { useUserJourneyContext } from '@context/UserJourneyContext';
import { logger as sentryLogger } from '@sentry/react';
import { CustomerDocumentUploadRequest } from '@services/documentStore/DocumentStore.types';
import DocumentStoreService from '@services/documentStore/DocumentStoreService';
import { EnvTag, getEnvTag } from '@utils/AppEnv';
import { Country } from '@utils/constants/localisation';
import { textBoxStyle } from '@utils/styles/textboxStyle';
import { DocumentDetail, documentDetailInitialState, documentDetailReducer } from '@views/IdentityVerificationDocumentUpload/documentDetailReducer';
import { ALLOWED_FILE_EXTENSIONS, FILE_ACCEPT_TYPES } from '@views/IdentityVerificationDocumentUpload/validateDocumentDetail';
import { useNavigate, useParams } from 'react-router-dom';
import { HoursDrivenPerWeek, TripsCompleted, UberTenure } from './UberProof.types';

/**
 * Validates the Uber proof form data.
 *
 * @param details - Form input
 * @param declarationChecked - Whether the declaration checkbox is checked
 * @returns Array of validation error messages (empty if valid)
 */
export const validateUberProof = (
    details: DocumentDetail,
    declarationChecked: boolean
): string[] => {
    const errors: string[] = [];

    // Validate required fields
    if (!details.frontFile) {
        errors.push('Uber Profile screenshot is required');
    }
    if (!details.uberTenure) {
        errors.push('Uber Tenure is required');
    }
    if (!details.drivingHoursPerWeek) {
        errors.push('Hours Driven per Week is required');
    }
    if (!declarationChecked) {
        errors.push('Declaration checkbox is required');
    }

    // Validate file type
    if (details.frontFile) {
        const isValidType = ALLOWED_FILE_EXTENSIONS.some(ext =>
            details.frontFile!.name.toLowerCase().endsWith(ext.toLowerCase())
        );
        if (!isValidType) {
            errors.push(`Uber Profile screenshot must be one of the following types: ${FILE_ACCEPT_TYPES}`);
        }
    }

    // Validate trips and rating (only required if not New to Uber)
    if (details.uberTenure !== UberTenure.NEW) {
        if (!details.tripsCompleted) {
            errors.push('Trips Completed is required');
        }
        if (details.uberRating === null) {
            errors.push('Uber Rating is required');
        } else if (isNaN(details.uberRating) || details.uberRating < 1 || details.uberRating > 5) {
            errors.push('Uber Rating must be between 1 and 5');
        }
    }

    return errors;
};

/**
 * Sanitises the Uber proof details based on tenure.
 * For "New to Uber", trips and rating are set to null.
 * For existing drivers, rating is rounded to 2 decimal places.
 *
 * @param details - Input document details
 * @returns Sanitised document details
 */
const sanitiseUberProofDetails = (details: DocumentDetail): DocumentDetail => {
    const sanitised = { ...details };

    if (sanitised.uberTenure === UberTenure.NEW) {
        // For new drivers, clear trips and rating
        sanitised.tripsCompleted = null;
        sanitised.uberRating = null;
    } else if (sanitised.uberRating !== null) {
        // For existing drivers, round rating to 2 decimal places
        sanitised.uberRating = Math.round(sanitised.uberRating * 100) / 100;
    }

    return sanitised;
};

export default function UberProof() {
    const { custId, country } = useParams() as { custId: string, country: Country };

    const navigate = useNavigate();
    const msalContext = useMsal();
    const isAuthenticated = useIsAuthenticated();
    const { formState } = useUserJourneyContext();

    useEffect(() => {
        if (!formState?.selectedPotential?.id) {
            navigate('/');
        }
    }, [formState, navigate]);

    const [declarationChecked, setDeclarationChecked] = useState(false);
    const [validationErrors, setValidationErrors] = useState<string[]>([]);
    const [wasSubmitted, setWasSubmitted] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [error, setError] = useState(false);

    const [uberProofDetails, uberProofDispatch] = useReducer(documentDetailReducer, documentDetailInitialState);

    const navigateToNextStep = () => navigate(`/onboarding/details`)

    const preventScrollIncrement = (event: React.WheelEvent<HTMLInputElement>) => {
        event.currentTarget.blur();
    };

    const handleSelectChange = (field: keyof DocumentDetail) => (event: React.ChangeEvent<HTMLSelectElement>) => {
        uberProofDispatch({
            type: 'UPDATE_FIELD',
            payload: {
                field,
                value: event.target.value,
            },
        });
    };

    const handleNumberInputChange = (field: keyof DocumentDetail) => (event: React.ChangeEvent<HTMLInputElement>) => {
        const parsed = parseFloat(event.target.value);
        const value = isNaN(parsed) ? null : parsed;
        uberProofDispatch({
            type: 'UPDATE_FIELD',
            payload: {
                field,
                value,
            },
        });
    };

    const handleFileChange = (event: FileEvent) => {
        uberProofDispatch({
            type: 'UPDATE_FIELD',
            payload: {
                field: 'frontFile',
                value: event.file,
            },
        });
    };

    // Should only run after HTML5 validation passes, but validating anyway
    const handleSubmit = (event: FormEvent) => {
        setWasSubmitted(true);
        event.preventDefault();

        const errors = validateUberProof(uberProofDetails, declarationChecked);
        setValidationErrors(errors);

        if (errors.length === 0) {
            setIsUploading(true);

            // Sanitise details for upload
            const sanitisedDetails = sanitiseUberProofDetails(uberProofDetails);

            // Build upload request from sanitised document details
            const uploadRequest = CustomerDocumentUploadRequest.fromDocumentDetail('Uber Profile', sanitisedDetails);
            // Set Zoho module information for Uber proof documents
            uploadRequest.zohoModule = 'Potentials';
            uploadRequest.zohoModuleUniqueId = formState.selectedPotential.id;

            sentryLogger.info(`Preparing Uber Profile upload:`, { uploadRequest });
            DocumentStoreService.prepareCustomerDocumentUpload(
                uploadRequest,
                { msalContext, isAuthenticated },
                country,
                custId,
            )
                .then(preparationResponse => {
                    sentryLogger.info(`Uber Profile upload prepared with ID: ${preparationResponse.document_store_record_id}`);
                    console.log('Presigned URLs received:', preparationResponse.presigned_upload_urls);

                    const uploadUrl = preparationResponse.presigned_upload_urls[0]?.presigned_upload_url;
                    if (!uploadUrl) {
                        throw new Error('No upload URL received');
                    }

                    const file = sanitisedDetails.frontFile!; // confirmed to exist by validate method

                    return DocumentStoreService.uploadFile(uploadUrl, file, file.type)
                        .then(uploadResult => {
                            if (!uploadResult.success) {
                                console.error('Upload failed:', uploadResult.error);
                                // TODO: Call cleanup endpoint
                                setError(true);
                                setWasSubmitted(false);
                                setIsUploading(false);
                            } else {
                                sentryLogger.info(`Uber Profile upload for ${preparationResponse.document_store_record_id} completed successfully`);
                                // This view comes right before Personal Details in both the old and FO2.0 flows
                                navigateToNextStep()
                            }
                        });
                })
                .catch(error => {
                    console.error('Upload preparation failed:', error);
                    setError(true);
                    setIsUploading(false);
                });
        } else {
            setWasSubmitted(false);
        }
    };

    return (
        <>
            <NavBar pageTitle='Uber Proof' />
            {isUploading &&
                <div
                    data-testid="loading-spinner"
                    style={{ display: 'flex', justifyContent: 'center', margin: '15vh' }}
                >
                    <Elliptical />
                </div>
            }
            {!isUploading &&
                <form style={{ margin: '2.5vh 2.5vw' }} onSubmit={handleSubmit}>
                    <div style={{ marginTop: '2vh', fontSize: '20px', fontWeight: 'bold' }}>
                        Uber Proof
                    </div>

                    {
                        validationErrors.length > 0 &&
                        <WarningBox
                            testId='validation-errors'
                            message={
                                <>
                                    <div>Please fix the following errors before submitting:</div>
                                    <ul style={{ marginTop: '0.5rem', marginBottom: 0, paddingLeft: '1.5rem' }}>
                                        {validationErrors.map((error, index) => (
                                            <li key={index}>{error}</li>
                                        ))}
                                    </ul>
                                </>
                            }
                        />
                    }

                    <div style={{ marginTop: '2vh' }}>
                        <Label text='Screenshot' requiredFieldInd />
                        <UploadFilePanel
                            file={uberProofDetails.frontFile || undefined}
                            allowedTypes={FILE_ACCEPT_TYPES}
                            required
                            testId='screenshot-file'
                            onSelect={handleFileChange}
                        />
                    </div>

                    <div style={{ marginTop: '2vh' }}>
                        <Label text='Uber Tenure' requiredFieldInd />
                        <DropDownMenu
                            menuName='uber-tenure'
                            testId='uber-tenure-dropdown'
                            required
                            choices={Object.values(UberTenure).map(choice => ({ value: choice, enabled: true }))}
                            onSelect={handleSelectChange('uberTenure')}
                        />
                    </div>

                    {uberProofDetails.uberTenure !== UberTenure.NEW && (
                        <div style={{ marginTop: '2vh' }}>
                            <Label text='Trips Completed' requiredFieldInd />
                            <DropDownMenu
                                menuName='trips-completed'
                                testId='trips-completed-dropdown'
                                required
                                choices={Object.values(TripsCompleted).map(choice => ({ value: choice, enabled: true }))}
                                onSelect={handleSelectChange('tripsCompleted')}
                            />
                        </div>
                    )}

                    {uberProofDetails.uberTenure !== UberTenure.NEW && (
                        <div style={{ marginTop: '2vh' }}>
                            <Label text='Uber Rating' requiredFieldInd />
                            <input
                                type='number'
                                name='uber-rating'
                                data-testid='uber-rating-input'
                                min='1'
                                max='5'
                                step='0.01'
                                required
                                onChange={handleNumberInputChange('uberRating')}
                                onWheel={preventScrollIncrement}
                                style={textBoxStyle}
                            />
                        </div>
                    )}

                    <div style={{ marginTop: '2vh' }}>
                        <Label text='Hours Driven per Week' requiredFieldInd />
                        <DropDownMenu
                            menuName='driving-hours'
                            testId='driving-hours-dropdown'
                            required
                            choices={Object.values(HoursDrivenPerWeek).map(choice => ({ value: choice, enabled: true }))}
                            onSelect={handleSelectChange('drivingHoursPerWeek')}
                        />
                    </div>

                    <div data-testid="declaration-panel" style={{ margin: '2.5vh 0' }}>
                        <Checkbox
                            id="declaration"
                            label={
                                `I, ${msalContext.accounts.at(0)?.name}, declare the customer has an Active Uber ` +
                                `Rideshare Driver Profile and the trips stated match their profile screenshot.`
                            }
                            required
                            onChange={setDeclarationChecked}
                        />
                    </div>

                    <div style={{ margin: '2.5vh 0' }}>
                        <SubmitBtn
                            text='Submit'
                            testId='submit-button'
                            disabled={wasSubmitted}
                        />
                    </div>

                    {
                        getEnvTag() === EnvTag.Production ||
                        <div style={{ margin: '2.5vh 0' }}>
                            <Button
                                label="Skip Uber Proof step"
                                btnType={ButtonStyle.tertiary}
                                onClickFunc={navigateToNextStep}
                            />
                        </div>
                    }
                </form>
            }
            {
                error &&
                <WarningModal
                    message='Something went wrong'
                    additionalInfo='We’re sorry, we were unable to process your request. Please try again later and if the issue persists, please log a ticket with IT Helpdesk.'
                    onClickClose={(event: any) => {
                        event.preventDefault();
                        setWasSubmitted(false);
                        setError(false);
                    }} />
            }
        </>
    )
}
